<template>
  <div class="companies">
    <Actions :items="closure.actions" :right="true" />
    <p v-if="connectionError" class="connection-status" role="status">{{ connectionError }}</p>
    <DocumentGenerationProgress
      v-for="item in activeGenerations"
      :key="item.id"
      :title="`Cierre ${dates.getPeriodToString(item.period)}`"
      :generation="item.documentGeneration"
    />
    <MobileTable
      v-model:selection="store.state.commonExpense.selectedClosures"
      :label="$route.name"
      :columns="closure.columns"
      :data="closureRows"
      :rows="50"
    />
    <DesktopTable
      v-model:selection="store.state.commonExpense.selectedClosures"
      :label="$route.name"
      :columns="closure.columns"
      :data="closureRows"
      :rows="50"
    />
  </div>
</template>

<script>
import { useRouter, useRoute } from "vue-router";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable";
import DesktopTable from "../../widgets/tables/DesktopTable";
import { onBeforeMount, onBeforeUnmount, reactive, computed, ref, watch } from "vue";
import store from "../../store";
import { openMode, actions, dates } from "../../constants";
import DocumentGenerationProgress from "../../components/shared/DocumentGenerationProgress.vue";
import { createClosurePoller, generationLabel } from "../../utils/closureProgress";

export default {
  components: { MobileTable, DesktopTable, Actions, DocumentGenerationProgress },

  setup() {
    const router = useRouter();
    const route = useRoute();
    const connectionError = ref("");
    const activeGenerations = computed(() => store.state.commonExpense.closures.filter(
      (item) => item.documentGeneration?.status === "running"
    ));
    const closureRows = computed(() => store.state.commonExpense.closures.map((item) => ({
      ...item,
      documentStatus: Number(item.statusId) >= 5 ? "Enviados" : item.documentGeneration
        ? `${generationLabel(item.documentGeneration)}${item.documentGeneration.total ? ` (${item.documentGeneration.completed}/${item.documentGeneration.total})` : ""}`
        : Number(item.statusId) === 4 ? "Listos para enviar" : "Pendientes",
    })));
    const poller = createClosurePoller({
      load: () => store.dispatch(actions.commonExpenseActions.findAllClosure, {
        buildingId: store.state.general.selectedBuilding,
        silent: true,
        updateStore: false,
      }),
      onData: (response) => {
        store.commit("setClosures", response.closures);
        connectionError.value = "";
        return activeGenerations.value.length > 0;
      },
      onError: (response, retrying) => {
        connectionError.value = retrying
          ? "No se pudo actualizar el avance. Reintentando la conexión automáticamente…"
          : response?.message || "No se pudo consultar el estado de los cierres.";
      },
    });
    const onCreate = () => {
      store.commit("setOpenMode", openMode.CREATE);
      router.push("/cierres/nuevo");
    };
    const onModify = () => {
      if (store.state.commonExpense.selectedClosures.length !== 1) {
        store.state.toast.add({
          severity: "info",
          summary: "",
          detail: "Debe seleccionar un registro!",
          life: 5000,
        });
      } else if (store.state.commonExpense.selectedClosures[0].statusId >= 5) {
        store.commit("setSelectedClosures", []);
        store.state.toast.add({
          severity: "info",
          summary: "",
          detail: "El cierre seleccionado ya fue enviado!",
          life: 5000,
        });
      } else {
        store.commit("setOpenMode", openMode.MODIFY);
        router.push({ path: "/cierres/nuevo", query: {
          cierre: store.state.commonExpense.selectedClosures[0].id,
          edificio: store.state.general.selectedBuilding,
        } });
      }
    };
    const onDetail = () => {
      if (store.state.commonExpense.selectedClosures.length !== 1) {
        store.state.toast.add({
          severity: "info",
          summary: "",
          detail: "Debe seleccionar un registro!",
          life: 5000,
        });
      } else {
        store.commit("setOpenMode", openMode.DETAIL);
        router.push({ path: "/cierres/nuevo", query: {
          cierre: store.state.commonExpense.selectedClosures[0].id,
          edificio: store.state.general.selectedBuilding,
          vista: "detalle",
        } });
      }
    };
    const onDownloadExcel = async () => {
      const request = {
        fileName: "Listado de cierres",
        headers: closure.columns.map((item) => item.header),
        data: store.state.commonExpense.closures.map((item) => [
          dates.getPeriodToString(item.period),
          closureRows.value.find((row) => row.id === item.id).documentStatus,
          item.prevBalance,
          item.payments,
          item.balance,
          item.surcharge,
          item.commonExpenses,
          item.extraFee,
          item.reserveFound,
          item.commonAreas,
          item.totalDebt,
        ]),
      };
      store.commit("setLoading", true);
      await store.dispatch("COMMON_EXPENSE_DOWNLOAD_EXCEL", request);
      store.commit("setLoading", false);
    };

    const closure = reactive({
      columns: [
        {
          field: "period",
          header: "Período",
          sort: true,
          period: true,
        },
        {
          field: "documentStatus",
          header: "Documentos",
          sort: true,
        },
        {
          field: "prevBalance",
          header: "Saldo mes anterior",
          sort: true,
          currency: true,
        },
        {
          field: "payments",
          header: "Pagos",
          sort: true,
          currency: true,
        },
        {
          field: "balance",
          header: "Saldo mes actual",
          sort: true,
          currency: true,
        },
        {
          field: "surcharge",
          header: "Recargo sobre deuda",
          sort: true,
          currency: true,
        },
        {
          field: "commonExpenses",
          header: "Gastos comunes",
          sort: true,
          currency: true,
        },
        {
          field: "extraFee",
          header: "Cuota extra",
          sort: true,
          currency: true,
        },
        {
          field: "reserveFound",
          header: "Fondo reserva",
          sort: true,
          currency: true,
        },
        {
          field: "commonAreas",
          header: "Áreas comunes",
          sort: true,
          currency: true,
        },
        {
          field: "totalDebt",
          header: "Total a pagar",
          sort: true,
          currency: true,
        },
      ],
      actions: [
        {
          name: "Nuevo",
          action: onCreate,
          hidden: router
            .getRoutes()
            .find((route) => route.path === "/cierres/nuevo"),
        },
        {
          name: "Modificar",
          action: onModify,
          hidden: router
            .getRoutes()
            .find((route) => route.path === "/cierres/nuevo"),
        },
        {
          name: "Detalle",
          action: onDetail,
          hidden: router
            .getRoutes()
            .find((route) => route.path === "/cierres/nuevo"),
        },
        {
          name: "Descargar Excel",
          action: onDownloadExcel,
          hidden: router
            .getRoutes()
            .find((route) => route.path === "/cierres/nuevo"),
        },
      ],
    });

    onBeforeMount(async () => {
      store.commit("setLoading", true);
      store.commit("setSelectedClosures", []);
      const finded = store.state.general.breadcrumbs.find(
        (item) => item.to === route.path
      );
      if (!finded)
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      await poller.start();
      store.commit("setLoading", false);
    });

    watch(() => store.state.general.selectedBuilding, () => {
      store.commit("setSelectedClosures", []);
      store.commit("setClosures", []);
      poller.start();
    });
    onBeforeUnmount(() => {
      poller.stop();
    });

    return { store, closure, closureRows, activeGenerations, connectionError, dates };
  },
};
</script>

<style scoped>
.connection-status { margin: 1rem; color: var(--primary-dark); text-align: center; }
</style>
